import { useTranslation } from "react-i18next";
import {
  Divider,
  List,
  ListItem,
  Tooltip,
  Typography,
} from "@material-ui/core";
import ItemIcon from "./ItemIcon";
import { useStyles } from "./styled";

import { Change, PropertyChanges, SectionConfig } from "./types";
import { FormatContext, formatValue } from "./state/formatValue";

interface Props {
  properties: PropertyChanges[];
  config: SectionConfig;
  formatContext: FormatContext;
}

export default function SectionGroup({
  properties,
  config,
  formatContext,
}: Props) {
  const { t } = useTranslation(["rules", "common"]);
  const classes = useStyles();

  const getPropertyLabel = (property: string) => {
    const { label, labelParams } = config.properties[property];
    const params = Object.entries(labelParams ?? {}).map(([name, key]) => [
      name,
      t(key),
    ]);

    return t(label, { field: property, ...Object.fromEntries(params) });
  };

  const formatProperty = (value: unknown, prop: string) =>
    formatValue(value, config.properties[prop].format, formatContext);

  const getChangeText = (change: Change, prop: string): string => {
    if (change.type === "added") return formatProperty(change.value, prop);
    if (change.type === "deleted") return formatProperty(change.value, prop);
    return t("confirmRuleUpdates.itemModified", {
      original: formatProperty(change.original, prop),
      update: formatProperty(change.update, prop),
      interpolation: { escapeValue: false },
    });
  };

  return (
    <List>
      <Typography className={classes.itemTitle}>
        {t(config.titleKey)}
      </Typography>
      {properties.map(({ property, changes }) => (
        <div key={property}>
          <ListItem className={classes.list} style={{ flexWrap: "wrap" }}>
            <Typography
              className={classes.propertyName}
              variant="subtitle2"
              color="textSecondary"
            >
              {getPropertyLabel(property)}
            </Typography>
            {changes.map((change, i) => {
              const text = getChangeText(change, property);
              return (
                <div key={i} className={classes.modifiedItemWrapper}>
                  <ItemIcon type={change.type} />
                  <Tooltip title={text}>
                    <Typography
                      className={classes.modifiedItem}
                      color="primary"
                      variant="subtitle2"
                    >
                      {text}
                    </Typography>
                  </Tooltip>
                </div>
              );
            })}
          </ListItem>
        </div>
      ))}
      <Divider className={classes.divider} />
    </List>
  );
}
